import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { frames, useMediaQuery } from '../../../../Styles';
import { PageHeader, FeedCurveCard, Overlay } from '../../../../Components';
import { NavigationFunction as RootNavigation, Pages } from "../../../../Navigation/root"
import { useAppSelector } from '../../../../Store';
import { FeedCurve, Graph_QL } from '../../../../Services/Agrisys_GqlDataCaller';
import { useFeedCurves } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves';
import { NewInputHandles } from '../../../../Definitions';
import CardsView from '../../../../Assets/TestData/cardsView';
import NewFeedcurve from '../../../../Components/FeedCurves/Modals/newFeedCurve';
import ShowFeedCurve from '../../../../Components/FeedCurves/Modals/showFeedCurve';

export default function FeedCurves() {
  const {t} = useTranslation()
  const navigator = useNavigate()
  const [selectedFeedcurve, setSelectedFeedcurve] = useState<FeedCurve | undefined>(undefined);
  const [copyFeedCurveMode, setCopyFeedCurveMode] = useState<boolean>(false);
  const [modalState, setModalState] = useState<"ShowFeedCurve" | "NewFeedcurve" | undefined>(undefined);
  const mobileXs = useMediaQuery(`(max-width: 690px)`);

  // #region | Data
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const domain = Graph_QL.ObjToString({domain: farm ?? ""});
  const connection = Graph_QL.create({domain: farm ?? ""});

  const {call: getFeedCurves, data: feedCurvesState, createFeedCurve, copyFeedCurve} = useFeedCurves(connection);
  const feedCurveNameInputRef = useRef<NewInputHandles>(null);
  
  const feedCurvesData = useMemo(() => {
    return feedCurvesState && feedCurvesState[domain] ? feedCurvesState[domain]?.data?.filter((feedCurve) => feedCurve.info?.deleted !== true) : [];
    },[domain, feedCurvesState])
    useEffect(() => {
      if (!feedCurvesState[domain] || (feedCurvesState[domain].data.length > 0 && !feedCurvesState[domain].data.find((feedCurves) => feedCurves.milestones !== undefined))) {
        getFeedCurves({fetchAmount: 50, ifFeedCurveInfo: true, ifSpreading: true})
      }
    },[domain, getFeedCurves, feedCurvesState])
  
  const feedCurvesIds = useMemo(() => {
    return feedCurvesData.map((feedCurve) => feedCurve.feedCurveId)
  },[feedCurvesData])
  // #endregion

  const onFeedCurveSelect = (id: number) => {
    const selected = feedCurvesData.find(fc => fc.feedCurveId === id);
    if (!selected) return;
  
    setSelectedFeedcurve(selected);
  
    if (mobileXs) {
      RootNavigation({ Navigator: navigator, navigation_page: Pages.feedCurves, navigation_tab: Pages.feedCurve, replace: true, navArgs: { selected: selectedFeedcurve?.feedCurveId }, currentDomain: farm })
    } else if (copyFeedCurveMode) {
      setModalState("NewFeedcurve");
    } else {
      setModalState("ShowFeedCurve");
    }
  }

  const handleCreateFeedCurve = async () => {
    if (feedCurveNameInputRef.current?.value) {
      setModalState(undefined);
      setSelectedFeedcurve(undefined);
      setCopyFeedCurveMode(false);
      const response = modalState === "NewFeedcurve" && selectedFeedcurve ? await copyFeedCurve(feedCurveNameInputRef.current.value.toString(), selectedFeedcurve.feedCurveId) : await createFeedCurve(feedCurveNameInputRef.current.value.toString());
      if (response.success) {
        RootNavigation({ Navigator: navigator, navigation_page: Pages.feedCurves, navigation_tab: Pages.feedCurve, replace: true, navArgs: { selected: response.feedcurve_id }, currentDomain: farm })
      }
    }
  }

  const FeedCurveModal = useMemo(() => {
    if (modalState === "ShowFeedCurve" && selectedFeedcurve && farm)

      return (<ShowFeedCurve
        farm={farm}
        navigateButton
        feedCurve={selectedFeedcurve}
        handleClose={() => setModalState(undefined)}
        feedCurveMap={{
          onNext: () => {
            const nextIndex = (feedCurvesIds.indexOf(selectedFeedcurve?.feedCurveId!) + 1) % feedCurvesIds.length;
            const next = feedCurvesData[nextIndex];
            if (next) setSelectedFeedcurve(next);
          },
          onPrev: () => {
            const prevIndex = (feedCurvesIds.indexOf(selectedFeedcurve?.feedCurveId!) - 1 + feedCurvesIds.length) % feedCurvesIds.length;
            const prev = feedCurvesData[prevIndex];
            if (prev) setSelectedFeedcurve(prev);
          }
        }}
      />
      )
    else return null;
  }, [farm, feedCurvesData, feedCurvesIds, modalState, selectedFeedcurve])

  return (
    <frames.Page_inner>
      {copyFeedCurveMode ? <Overlay overrides={{ zIndex: 999 }} /> : <></>}
      <PageHeader
        topHeader={t(`menuHeaders.${Pages.feedCurves}`, { defaultValue: "feed curves" })}
        subHeader={t("menuHeaders.overview", { defaultValue: "overview" })}
        optionButtons={{
          contents: [copyFeedCurveMode ? t("buttons.cancel", {defaultValue: "cancel"}) : t("feedCurvePages.feedCurves.copyFeedCurve", {defaultValue: "copy feed curve"})],
          onClick: (i => {
            if (i === 0) {setCopyFeedCurveMode(copyFeedCurveMode ? false : true)}
          })
        }}
      />

      <frames._page_inner_content>
        <frames._page_inner_content_scroll zIndex={copyFeedCurveMode ? 9999 : 0}>
          
          <div style={{position: copyFeedCurveMode? "relative" : "unset"}}>
            <CardsView
              items={feedCurvesData.map((item, index) => (
                <FeedCurveCard
                  key={index}
                  feedCurve={item}
                  onFeedCurveSelect={onFeedCurveSelect}
                  farm={farm}
                  expand={copyFeedCurveMode ? false : true}
                />
              ))}
              cardBtn={{
                content: t("feedCurvePages.feedCurves.createFeedCurve", { defaultValue: "create feed curve" }),
                subContent: t("feedCurvePages.feedCurves.createFeedCurveFromScratch", { defaultValue: "Create feed curve from scratch" }),
                onCardBtnClick: () => setModalState("NewFeedcurve"),
                disabled: copyFeedCurveMode
              }}
            />
          </div>
        </frames._page_inner_content_scroll>
      </frames._page_inner_content>

      {/* Popup Modal for clicked feedcurve */}
      {FeedCurveModal}

      {/* Add / Copy Feed Curve Modal */}
      {modalState === "NewFeedcurve" &&
        <NewFeedcurve
          copyFeedcurve={selectedFeedcurve}
          handleClose={() => {setModalState(undefined); setSelectedFeedcurve(undefined)}}
          handleCreateFeedCurve={handleCreateFeedCurve}
          feedCurveNameInputRef={feedCurveNameInputRef}
        />
      }
    </frames.Page_inner>
  );
}  